/**
 * Shared status, urgency, and role constants for Crew Forge
 *
 * Single source of truth for:
 *   - ORDER_STATUSES   – canonical list of all order statuses
 *   - STATUS_LABELS    – human-readable status names (re-exported from colors.js)
 *   - URGENCY_PRIORITY – sort-weight map for urgency values
 *   - ROLE_LABELS       – human-readable role names
 */
// 🔻 `URGENCY_LABELS` came OFF this re-export 2026-08-18 — deleted from
// colors.js with `UrgencyBadge.js` (owner ruling). It was read only there.

// Re-export labels already defined in colors.js to avoid duplication
export { STATUS_LABELS } from './colors';

// ---------------------------------------------------------------------------
// Order Statuses – canonical array (includes cancelled)
// Used in: AdminOrders, DepartmentOrders, MyOrders status filters
// ---------------------------------------------------------------------------
export const ORDER_STATUSES = [
  'submitted',
  'approved',
  'rejected',
  'ordered',
  'partially_delivered',
  'delivered',
  'cancelled',
];

// ---------------------------------------------------------------------------
// Delivery-stage statuses — the statuses on which the received-tick SECTION
// is shown (rendered, though not necessarily interactive) and delivery
// paperwork is meaningful: approved / ordered / partially_delivered /
// delivered.
//
// ⚠️ UNTIL 2026-08-16 THIS ARRAY ALSO MATCHED THE RECEIVE ENDPOINTS' STATUS
// GATE EXACTLY, AND THE COMMENT SAID SO — that is now FALSE and is not a
// drift to "fix" back. Owner: *"I think we should have the 'mark as
// delivered' tick box available until the order is marked as ordered. maybe
// grey out the tick box with a tool tip"* — the section must stay VISIBLE on
// `approved` (discoverable, greyed) even though the server no longer accepts
// a receive on it. This array now answers "does the section show", never
// "may a tick be sent" — that second, NARROWER question is
// `RECEIVE_ENABLED_STATUSES` below, which is what actually matches the
// receive endpoints' gate. Do not fold the two back into one array: doing so
// either hides the section on `approved` (against the ruling) or re-widens
// the endpoints (a live write nobody asked to restore).
//
// ⚠️ It is deliberately WIDER than the server's OWN `DELIVERY_STAGE_STATUSES`
// (`server/helpers/orderAccess.js`), which OMITS `approved` and answers a
// DIFFERENT question — which orders a stores user may SEE (`canViewOrder`'s
// stores term + the stores list filters). This comment used to claim it
// "mirrors" that export; it never did — same name, different set. Do not
// "sync" the two: adding `approved` there widens stores visibility, and
// removing it here hides the greyed section on approved orders.
//
// Client consumers: `OrderExpandedDetails` gates its Items Received block on
// this array (on the DATA, `order.status`), and `OrderPage.js`'s
// `canTrackDelivery` imports it too (since 2026-08-15 — it carried an inline
// copy of the same four statuses until then; pinned by
// `pages/OrderPage.deliveryStages.test.js`).
// ---------------------------------------------------------------------------
export const DELIVERY_STAGE_STATUSES = Object.freeze([
  'approved',
  'ordered',
  'partially_delivered',
  'delivered',
]);

// ---------------------------------------------------------------------------
// Receive-ENABLED statuses — the statuses on which a LINE may be received.
// 🔀 PER LINE SINCE 2026-10-28 (owner ruling A): *"a line can be marked
// delivered as soon as THAT line is ordered"*. So the order-level half of the
// rule is "approved or later" — byte-for-byte the server's
// `LINE_ORDERABLE_STATUSES` (`server/helpers/deliveryStatus.js`), which gates
// the two receive endpoints AND the two ordered endpoints — and the line-level
// half is `deliveredControlState` in `components/OrderItemReceivedControls.js` (a line
// offers "Delivered" only once it is ordered; the server's `line_not_ordered`).
// Until 2026-10-28 this was `ordered`/`partially_delivered`/`delivered` and
// greyed every tick on `approved`; that order-level lock is gone with the
// per-line step (an unordered line now shows "Ordered", never a greyed
// "Delivered"). It now holds the SAME four values as `DELIVERY_STAGE_STATUSES`
// and is kept as its own name because the two still answer different
// questions (may a line be received / does the section show) — if one moves,
// decide for the other separately.
//
// Client consumers: the pages resolve `canReceiveNow` from it, and
// `OrderTableView`'s `receiptsMayRender` (a receipt only renders on a status
// where it can legitimately exist — an approved order can now hold one).
// ---------------------------------------------------------------------------
export const RECEIVE_ENABLED_STATUSES = Object.freeze([
  'approved',
  'ordered',
  'partially_delivered',
  'delivered',
]);

// The greyed "Ordered" tick's tooltip, for a viewer who may RECEIVE but not
// ORDER (Stores, the requester — the server's `requireOrderManagement`). ONE
// definition for every surface.
export const RECEIVING_LOCKED_TOOLTIP =
  'Whoever manages this department\'s orders marks a line as Ordered — it can then be marked delivered.';

// ---------------------------------------------------------------------------
// MANAGER CANCEL — the statuses an ORDER MANAGER may cancel from (2026-09-11).
//
// 🔑 THIS IS `DepartmentOrders.js`'s CANCEL GATE, WRITTEN DOWN. That page has no
// Cancel menu ITEM at all: cancelling is reachable only through the status
// override's `cancelled` option, which `InlineStatusJump` delegates to the
// page's cancel form (`onCancelClick`). So a department manager's gate is
// exactly two terms — `useOrderAuthority().canManageOrder(order)`, which renders
// the override, and "the target is not the status it already has", which is what
// makes `cancelled` a no-op revert on an already-cancelled order. That is the
// gate `MyOrders.js`'s Cancel item reuses for a My Space owner, per the owner's
// *"I think they should have access to all of it"* (2026-09-11) — an outside
// buyer cancelling an already-placed order is the case he described.
//
// 🔴 `DepartmentOrders.js` DELIBERATELY DOES NOT IMPORT THIS, and that is not
// drift. Its route carries NO status term to replace — the term is implicit in
// the `<select>`'s current-value revert — so importing it there would mean
// ADDING a condition, i.e. changing that page's behaviour to share a constant.
// The constant exists so the second surface states the rule instead of
// inlining a bare `!== 'cancelled'` nobody can trace back to this one.
//
// 🪤 DERIVED FROM `ORDER_STATUSES`, never retyped: a status added to that array
// is cancellable by a manager unless somebody deliberately excludes it here, so
// this cannot silently go stale the way a hand-written sibling list would.
// `cancelled` is the sole exclusion, and it matches the SERVER exactly —
// `PATCH /api/orders/:id/cancel` answers 400 "Order is already cancelled"
// before it looks at authority at all.
export const MANAGER_CANCELLABLE_STATUSES = Object.freeze(
  ORDER_STATUSES.filter((s) => s !== 'cancelled')
);

/**
 * May an ORDER MANAGER cancel this order from the status it is in?
 *
 * ⚠️ THE STATUS HALF ONLY — it says nothing about authority. Every caller must
 * pair it with `useOrderAuthority().canManageOrder(order)`, which is the half
 * that decides WHO. Folding the two together here would put an authority
 * decision inside a constants module, which is the thing
 * `hooks/useOrderAuthority.js` exists to keep in one place.
 */
export const managerMayCancelOrder = (order) =>
  !!order && MANAGER_CANCELLABLE_STATUSES.includes(order.status);

/**
 * May the order's own REQUESTER (not a manager) cancel it from this status?
 * The My Orders Cancel item's requester arms, extracted 2026-10-01 when
 * `OrderPage.js` became a second caller (its edit-mode only-line Remove opens
 * that page's cancel form). A department order: `submitted` / `approved` only
 * (the client offers no more than that; `PATCH /api/orders/:id/cancel` refuses
 * a non-manager on `ordered`/`partially_delivered`/`delivered`).
 * The legacy NULL-`department_id` solo shape: anything but `cancelled` /
 * `delivered`. ⚠️ STATUS ONLY, like `managerMayCancelOrder` — the caller must
 * already know the viewer IS the requester.
 */
export const requesterMayCancelOrder = (order) => {
  if (!order) return false;
  return order.department_id
    ? (order.status === 'submitted' || order.status === 'approved')
    : (order.status !== 'cancelled' && order.status !== 'delivered');
};

// ---------------------------------------------------------------------------
// Urgency – sort-weight map (lower = more urgent)
// Used in: AdminOrders, DepartmentOrders for urgency-based sorting
// ---------------------------------------------------------------------------
export const URGENCY_PRIORITY = {
  same_day: 1,
  next_day: 2,
  '2_3_days': 3,
  '7_days': 4,
  custom: 5,
};

// ---------------------------------------------------------------------------
// Role groups – mirrors server/helpers/roles.js on the client side
// ---------------------------------------------------------------------------
export const APPROVAL_ROLES = ['admin', 'supervisor', 'sub_department_lead', 'buyer'];
// Roles that see the Department / Sub-Dept Orders view
export const DEPARTMENT_ROLES = ['sub_department_lead', 'supervisor'];

// ---------------------------------------------------------------------------
// Roles – human-readable labels
// Used in: Layout, MyOrders, DepartmentDashboard, ProductionDepartmentSwitcher
// ---------------------------------------------------------------------------
export const ROLE_LABELS = {
  admin: 'Admin',
  buyer: 'Buyer',
  supervisor: 'Supervisor',
  sub_department_lead: 'Sub Dept Lead',
  user: 'User',
};

// ---------------------------------------------------------------------------
// Approval-context role labels – admin shown as "Admin/Buyer" because both
// roles can action approval steps.
// Used in: ApprovalStatus, ApprovalWorkflowManager, orderStepInfo
// ---------------------------------------------------------------------------
export const APPROVAL_ROLE_LABELS = {
  ...ROLE_LABELS,
  admin: 'Admin/Buyer',
};

/**
 * `approverRoleLabel(role)` — how an approval step's role is NAMED to a human,
 * extracted 2026-09-11 so the two surfaces that say it cannot drift.
 *
 * 🔑 TWO CALLERS, ONE MAP, DELIBERATELY DIFFERENT PUNCTUATION:
 *   - `utils/orderStepInfo.js#getNextStepInfo` — *"Waiting for approval from
 *     Jake Dimmer Supervisor"*, a full sentence in the step panel;
 *   - `constants/rowStyles.js#chevronTitle` — *"Awaiting Jake Dimmer
 *     (Supervisor)"*, a hover tooltip on a 40px target.
 *   The LABEL is shared; the sentence around it is each surface's own. That is
 *   not drift and should not be "unified" — they are different registers.
 *
 * 🪤 THE FALLBACK CHAIN IS PART OF THE ANSWER, NOT A DETAIL. An unmapped
 * role (a future `coordinator`) is still better SAID than swallowed, so the raw
 * value is used; only a missing/empty role reaches `'Approver'`. Rendering
 * `"Awaiting Pat ()"` would be worse than either.
 *
 * 🔴 ONE EXISTING SITE DELIBERATELY DOES **NOT** USE THIS, and that is a
 * scope decision rather than an oversight. `getNextStepInfo`'s DEPT-view branch
 * ends `|| ''` instead of `|| 'Approver'`, because there the label is appended
 * to a name in a longer sentence where a literal "Approver" reads wrongly.
 * Switching it would change a shipped sentence on a surface this change has no
 * business touching. If that branch is ever revisited, decide its fallback
 * there — do not quietly widen this helper to cover it.
 *
 * @param {string|null|undefined} role
 * @returns {string}
 */
export function approverRoleLabel(role) {
  return APPROVAL_ROLE_LABELS[role] || role || 'Approver';
}

// ---------------------------------------------------------------------------
// Equipment Listing Statuses
// Used in: MarketplaceMyListings
// ---------------------------------------------------------------------------
export const LISTING_STATUSES = ['active', 'paused', 'removed'];

// Re-export listing status labels/colors from colors.js
export { LISTING_STATUS_COLORS, LISTING_STATUS_LABELS } from './colors';

// ---------------------------------------------------------------------------
// TIME-CRITICAL CHROME — "is this order still racing a clock?"
// ---------------------------------------------------------------------------
/**
 * `showsTimeCriticalChrome(order)` — THE predicate behind every indicator that
 * only means something while an order is still in flight: the urgency badge,
 * and the lead-time dot on the table (the card's lead-time pill went with the
 * order cards, 2026-10-18).
 *
 * A `delivered` order has arrived, a `cancelled` one is not coming, and a
 * `rejected` one has been bounced back to be resubmitted — none of the three
 * is racing a live deadline, so "needed in 2 days" and "lead time risk" are
 * all noise on them — worse than noise, because a red marker reads as a live
 * problem that does not exist.
 *
 * 🔀 **EXTRACTED 2026-08-08, and it did not exist before — it was ALREADY
 * DUPLICATED VERBATIM IN TWO FILES.** The (since retired) card row and `OrderTableView.js`
 * each declared their own `const showUrgencyChrome = order.status !==
 * 'cancelled' && order.status !== 'delivered'`, byte-identical, independently.
 * Adding the lead-time dot would have made it a third copy, which is what the
 * Code Unification Principle exists to stop — so it became one function first.
 *
 * ⚠️ **The old NAME was already wrong and is deliberately not carried over.**
 * `showUrgencyChrome` described its first caller, not the question: it gates the
 * lead-time pill too, and now a dot that has nothing to do with urgency. A name
 * that describes one consumer is how a shared predicate acquires a second
 * meaning nobody notices.
 *
 * 🔑 **A missing or unknown status shows the chrome.** The list endpoints always
 * send a status, so this is not a live case — but failing OPEN is the right
 * default here: an order in an unrecognised state is one nobody has confirmed is
 * finished, and hiding a deadline warning is the costlier error.
 *
 * ⚠️ **`rejected` JOINED THE HIDDEN SET 2026-08-17, REVERSING THE RULING THIS
 * PARAGRAPH USED TO STATE — do not "restore" the old exclusion as a fix.**
 * Owner: a rejected order was bounced back to be resubmitted, it is not a
 * live deadline, and the urgency badge / lead-time pill / table dot make it
 * wrongly read as an active order. A rejected order is not FINISHED — it has
 * been bounced back to its requester to fix and resubmit (`PUT /api/orders/:id`
 * sets `newStatus = 'submitted'` for a rejected order) — but "not finished"
 * and "racing a live deadline" are different claims, and only the second is
 * what this predicate answers. `MyOrders` still builds its "N to resubmit"
 * count from exactly this status (`status === 'rejected'`, a SEPARATE
 * predicate in `pages/MyOrders.js`, untouched by this change) — that count is
 * the right way to say "these need attention"; the countdown chrome is not.
 * **Cancelled, delivered and rejected are all terminal for THIS predicate's
 * question — none of the three has a deadline still to race.**
 *
 * 🚨 **IT IS A DISPLAY GATE AND NOTHING ELSE — never grow a permission term into
 * it.** The sanctioned-exception box in CLAUDE.md forbids re-deriving "who may do
 * what" inside `OrderTableView.js`, and a predicate imported by several
 * surfaces is exactly where such a term would go unnoticed.
 *
 * 🔀 **THIS WAS EXTRACTED TWICE, IN PARALLEL, ON 2026-08-08 — once to here and
 * once to `constants/rowStyles.js` as `TERMINAL_ORDER_STATUSES` +
 * `showsTimeCriticalChrome`, by two branches that could not see each other.**
 * Both landed in one merge, both components then imported the same name from two
 * modules, and a duplicate `import` binding is a **SyntaxError** — so the merge
 * produced a tree that could not build, from two diffs that were each correct.
 * The rowStyles copy is deleted and its reasoning (the `rejected` and
 * display-gate paragraphs above) moved here, so nothing was lost with it. The
 * rowStyles version argued it belonged there "because both layouts already
 * import from that file"; that is true of this file too, and the deciding point
 * is the NAME — the predicate is about a STATUS, which is what this module is.
 *
 * Pinned in BOTH directions by `client/src/components/orderRowTimeChrome.test.js`
 * — a suppression test that only checks absence would pass just as happily when
 * the badge has been deleted outright.
 *
 * 🔻 **THE LIST STAYS A LIST OF STATUSES — COMPLETION IS A SEPARATE AXIS**
 * (owner ruling, 2026-09-13). `isOrderComplete` is an administrative
 * disposition layered on top of a status and never touches `status` (see its
 * own docblock below), so it can never be an entry here; it is a second term
 * in the PREDICATE instead. The case that forced it: an order closed as-is
 * while `partially_delivered` is not in this list, so it kept its lead-time
 * dot on the Completed tab — and since the 2026-09-13 delivery-band ruling
 * (`components/LeadTimeRisk.js`) that dot would have gone RED, chasing a
 * delivery the buyer has already decided will never come.
 * 🔑 ONE GATE, not four conditions: every caller reads this predicate and
 * inherits the rule — `OrderTableView.js`,
 * `ExpectedDeliveryBadge.js` and `pages/OrderPage.js`, each of which passes
 * the WHOLE order (every orders endpoint is a `SELECT o.*`, so `completed_at`
 * is on the row; an object without it is simply "not complete", which is the
 * pre-2026-09-13 behaviour and therefore safe).
 *
 * @param {{ status?: string, completed_at?: string|null }} order
 * @returns {boolean}
 */
export const TIME_CRITICAL_HIDDEN_STATUSES = Object.freeze(['delivered', 'cancelled', 'rejected']);

export function showsTimeCriticalChrome(order) {
  if (isOrderComplete(order)) return false;
  return !TIME_CRITICAL_HIDDEN_STATUSES.includes(order?.status);
}

/**
 * Statuses where the order has been PLACED with the supplier. The buyer has
 * acted, so the "order within X" chrome no longer applies — instead the
 * table's lead-time dot goes green and its Date-cell tooltip switches to an
 * "ordered / expected delivery" reading (owner ruling 2026-08-14).
 * 🔻 **"GOES GREEN" IS NO LONGER UNCONDITIONAL, 2026-09-13:** once placed, the
 * dot follows the DELIVERY date — red when it has passed, amber within 2 days,
 * and green only beyond that (`components/LeadTimeRisk.js`). This list is
 * unchanged; it still just answers "has the buyer acted?". ONE
 * definition, imported by `LeadTimeDot` (`LeadTimeRisk.js`) and
 * `OrderTableView` (its date-cell tooltip still reads `placed`) — never
 * inline the status list. `partially_delivered` counts as placed (some has
 * arrived, the rest is on its way); `delivered`/`cancelled` are already
 * handled by `showsTimeCriticalChrome`, which this composes on top of.
 *
 * 🔻 UNTIL 2026-08-18 this also said *"and the countdown `UrgencyBadge` is
 * suppressed on BOTH order-list layouts (table 2026-08-14; card 2026-08-15,
 * owner-approved parity)"* and listed `OrderListRow` as a THIRD importer.
 * `UrgencyBadge` was RETIRED that day (owner ruling — see CLAUDE.md's
 * `LeadTimeRisk.js` bullet) and the card row stopped importing this
 * function — its only use there was that badge's `!isPlaced` gate.
 */
export const PLACED_ORDER_STATUSES = Object.freeze(['ordered', 'partially_delivered']);

export function isPlacedStatus(status) {
  return PLACED_ORDER_STATUSES.includes(status);
}

// ---------------------------------------------------------------------------
// ORDER COMPLETION (2026-08-16) — a completion-flag OVERLAY, never a status.
// Design: docs/superpowers/specs/2026-08-16-order-completion.md.
// ---------------------------------------------------------------------------
/**
 * `isOrderComplete(order)` — THE ONE predicate behind the `✓ Complete` badge
 * in the table (`OrderTableView.js`), and behind the Mark-complete/Re-open action's own state read.
 *
 * 🔑 Server truth is `orders.completed_at IS NOT NULL` — an administrative
 * disposition layered ON TOP OF a delivered order, not a lifecycle stage.
 * `status` is never touched by completion and this predicate never reads it.
 *
 * 🪤 Checked with `!= null` in spirit (`Boolean(...)`, never a truthiness
 * shortcut like `!!order.completed_at.length`) because the field arrives as
 * an ISO datetime STRING from the API — a non-empty string is already
 * truthy, so the simple `Boolean()` coercion is correct here without the
 * `0`-is-a-legitimate-value trap this codebase's other predicates carry
 * (`lead_time_days`, `file_count`). There is no falsy-but-meaningful value on
 * this field: it is either a real timestamp or it is `null`/`undefined`.
 *
 * @param {{ completed_at?: string|null }|null|undefined} order
 * @returns {boolean}
 */
export function isOrderComplete(order) {
  return Boolean(order && order.completed_at);
}

/**
 * `deliveredOrderPrompt(order)` — THE ONE predicate behind the delivered-order
 * PROMPT pills, in `components/OrderTableView.js`'s status cell, rendered by
 * `components/DeliveredOrderPill.js`. Added 2026-09-11.
 *
 * Returns `'no_paperwork'` | `'ready'` | `null`.
 *
 * 🔑 THE PROBLEM IT SOLVES, IN THE OWNER'S WORDS: *"the buyer doesn't know WHY
 * a delivered order is still not completed"*. `status` and `completed_at` are
 * INDEPENDENT columns — see `isOrderComplete` directly above, whose docblock
 * records that completion is *"an administrative disposition layered ON TOP OF a
 * delivered order, not a lifecycle stage"* — so a row can sit at `delivered`
 * indefinitely with nothing on screen saying what is outstanding.
 *
 * 🚨 THESE ARE PROMPTS, NEVER GATES, AND THAT IS A RULING RATHER THAN AN
 * IMPLEMENTATION CHOICE. Owner, 2026-09-11: *"marking as completed can be done
 * after delivery and/or after paperwork is uploaded. Some buyers will upload
 * paperwork others will not."* So paperwork is OPTIONAL: `'no_paperwork'` is not
 * an error state and must never be drawn as one (no red, no warning triangle),
 * and NOTHING here may ever be wired into whether Mark as Complete is offered.
 * A future reader who turns this into a precondition has reversed the ruling.
 *
 * 🪤 `has_delivery_paperwork === true`, NEVER A TRUTHINESS TEST, AND A MISSING
 * FIELD RENDERS NOTHING. The three list endpoints always send the boolean —
 * `routes/orders.js` (both list blocks) and `routes/approvals.js`, all via
 * `orderFileTooltipFields` — and that file's own comment states the rule this
 * follows: *"a missing field must not look like 'no files'"*. So `undefined`
 * (a fourth endpoint that forgets the field, a hand-built fixture, an optimistic
 * row patched in by the client) yields `null` and the row is UNCHANGED, rather
 * than asserting "no paperwork has been uploaded" about an order nobody asked.
 * Fail QUIET: the cost of a missing prompt is a buyer who has to open the order;
 * the cost of a wrong one is a buyer who uploads a duplicate delivery note.
 *
 * 🔴 `partially_delivered` IS DELIBERATELY EXCLUDED. Neither prompt is true of
 * it: more is still coming, so "no paperwork yet" is not a thing to act on and
 * "ready to complete" is false. The server agrees — `PATCH /:id/complete` admits
 * `delivered` AND `partially_delivered`, so this is the CLIENT choosing to
 * prompt on only one of the two, not a mirror of the server gate. Do not
 * "complete the set".
 *
 * @param {{ status?: string, completed_at?: string|null, has_delivery_paperwork?: boolean }|null|undefined} order
 * @returns {'no_paperwork'|'ready'|null}
 */
export function deliveredOrderPrompt(order) {
  if (!order || order.status !== 'delivered') return null;
  // A completed order has nothing outstanding — the ✓ Complete badge stands
  // alone. Same shared predicate, never a second read of `completed_at`.
  if (isOrderComplete(order)) return null;
  if (order.has_delivery_paperwork === true) return 'ready';
  if (order.has_delivery_paperwork === false) return 'no_paperwork';
  return null;
}

// ---------------------------------------------------------------------------
// COMPLETION FILTER (2026-08-16, REDESIGNED THE SAME DAY) — a single
// "Completed" toggle PILL living in the status checkbox list on Dept Orders +
// My Orders, not a separate three-state radiogroup. It replaced a THREE-state
// `completionFilter` ('all' | 'hide' | 'only') that shipped and was withdrawn
// within hours.
//
// 🚨 **WHY IT WAS WITHDRAWN — the "only completed" state is what made
// unticking-all-received-items on a completed order look like it "closed the
// expanded row".** Un-receiving every item on a delivered/partially-delivered
// order clears `completed_at` in the same transaction (see
// `server/helpers/orderCompletionClear.js`, "clear-on-push-back"), so the
// order stops being `isOrderComplete`. Under the withdrawn `'only'` view that
// pushed the row straight out of the filtered list — the row's own expand
// state (`expandedOrders`) was never touched, but the row vanished from the
// DOM anyway, which reads as "the expanded row collapsed". **Reproduced and
// confirmed against the DEFAULT (unfiltered) view: nothing collapses there —
// `COMPLETION_FILTER_ALL`'s old predicate, and `showCompleted: true` here,
// always includes the order regardless of `completed_at`.** So this is a
// FILTER defect, not a row/remount bug, and there is now no "only completed"
// state to reproduce it under.
//
// 🔑 It is a VIEW composing with the status multi-select (intersection),
// exactly as the two-state control did — never a member of `ORDER_STATUSES`.
// Completion is an OVERLAY on top of status, not a status value of its own —
// do not fold it into the enum just because it is now presented as a pill
// beside the status pills.
// ---------------------------------------------------------------------------
export const COMPLETION_FILTER_ALL = 'all';
export const COMPLETION_FILTER_HIDE = 'hide';

/**
 * THE predicate behind the completion filter — composes with `isOrderComplete`
 * and never re-derives "is complete" itself.
 *
 * An order passes iff `showCompleted` is true (nothing is hidden) OR the
 * order simply isn't complete (so there is nothing for the toggle to hide).
 * There is no "only completed" reading any more — offered and withdrawn the
 * same day it shipped; see the block comment above.
 *
 * @param {{ completed_at?: string|null }} order
 * @param {boolean} showCompleted
 * @returns {boolean}
 */
export function matchesCompletionFilter(order, showCompleted) {
  return showCompleted || !isOrderComplete(order);
}

/**
 * 🪤 **STORAGE MIGRATION — this key has now held THREE shapes and this
 * function reads all of them forward to the current boolean.** `localStorage`
 * (`sfx_dept_orders_hide_completed` / `sfx_my_orders_hide_completed`) has
 * held, in order: the ORIGINAL two-state control's literal `"true"`/`"false"`
 * (`"true"` = hidden); the withdrawn three-state enum `"all"`/`"hide"`/
 * `"only"`; and now this — the SAME two literal values the original two-state
 * control wrote (`COMPLETION_FILTER_ALL` = shown, `COMPLETION_FILTER_HIDE` =
 * hidden), via `serializeShowCompleted` below. A saved `"hide"` therefore
 * survives both migrations unchanged. `"true"` (legacy) and the withdrawn
 * `"only"` are the only two raw values that must be told apart from a bare
 * boolean read: `"true"` means hidden (legacy), `"only"` has no meaning any
 * more and — like `"all"`, `"false"`, absent, or anything unrecognised —
 * falls open to SHOWN, the unchanged default.
 *
 * @param {string|null} raw — the literal `localStorage.getItem(...)` result
 * @returns {boolean} showCompleted
 */
// 🔻 OWED WORK (2026-09-30): with My Orders on tabs, `matchesCompletionFilter`,
// `normalizeStoredShowCompleted` and `serializeShowCompleted` have NO
// production importer. Still exported and tested; removing three public
// exports is its own change, not smuggled into the tabs one.
export function normalizeStoredShowCompleted(raw) {
  if (raw === 'true' || raw === COMPLETION_FILTER_HIDE) return false;
  return true;
}

/**
 * The inverse of `normalizeStoredShowCompleted` — what to WRITE back to
 * `localStorage`. Reuses the enum's own two surviving tokens rather than the
 * bare `"true"`/`"false"` the legacy control wrote, so a value this function
 * writes is never re-read as the LEGACY meaning of `"true"` (which means
 * hidden, not shown) by `normalizeStoredShowCompleted` above.
 *
 * @param {boolean} showCompleted
 * @returns {'all'|'hide'}
 */
export function serializeShowCompleted(showCompleted) {
  return showCompleted ? COMPLETION_FILTER_ALL : COMPLETION_FILTER_HIDE;
}

// ---------------------------------------------------------------------------
// ORDER QUEUE TABS (2026-09-12) — the stage tab strip on Department Orders.
//
// Owner's ruling: tabs "like on admin settings", but named by QUEUE so no
// stage doubles — **All · To approve · To order · To receive · To complete ·
// Completed · Cancelled**.
//
// 🔁 **THE SEVENTH TAB IS A REVERSAL OF THE SAME DAY'S OWN RULING, AND THE
// REASON IS ARITHMETIC RATHER THAN TASTE.** 2026-09-12 morning: *"leave
// rejected and cancelled in All only"*, and it shipped as six tabs. That
// afternoon Pete read the strip on production — **All 45 · To approve 6 ·
// To order 0 · To receive 13 · To complete 21 · Completed 0** — saw the six
// queues sum to 40 against an All of 45, and asked for *"a tab for the cancled
// orders"*. The five missing rows were the rejected and cancelled ones the
// morning ruling had left homeless.
//
// 🔑 **ONE TAB HOLDS BOTH `cancelled` AND `rejected`, AND THAT IS WHAT MAKES
// THE COUNTS ADD UP EXACTLY.** Two tabs would also have closed the gap; one
// was chosen because the pair is the same thing to a reader — dead work, not
// in anybody's queue — and because every recognised status then maps to
// exactly one queue, so `All` is the sum of the six. **The sum identity is the
// point of this tab, not a side effect of it**, and it is pinned as such by §2
// of `DepartmentOrders.queueTabs.test.js`. It holds only for RECOGNISED
// statuses; see the `null` note on `orderQueueTab` below.
//
// 🔑 A QUEUE NAME IS NOT A STATUS NAME, AND THAT IS THE WHOLE POINT OF THE
// VOCABULARY. `to-order` is the queue of orders that have been APPROVED and
// are waiting for somebody to place them; `to-receive` holds both `ordered`
// and `partially_delivered` because in both the goods are outstanding. Naming
// the tabs after statuses would have produced two tabs called "Delivered"
// (the status, and the completion overlay on top of it), which is the
// doubling the owner's wording exists to prevent.
// 🪤 `Cancelled` is the ONE label that is also a status name, and it is not an
// exception to that rule: it is the queue name for the pair, and `rejected`
// rows live under it. Do not read it as `status === 'cancelled'`.
//
// 🚨 COMPLETION IS AN OVERLAY, NOT A STAGE — `orders.completed_at` and
// `orders.status` are INDEPENDENT columns (see `isOrderComplete` above), so
// `completed` is the one tab that cuts ACROSS the status axis. That is why it
// is tested FIRST in the mapping below, and why `isOrderComplete` must never
// join `ORDER_STATUSES`.
//
// 🔻 THIS ARRAY WAS THE PERSISTED VALUE VOCABULARY UNTIL 2026-09-13, when
// `sfx_dept_orders_tab` was RETIRED by owner ruling ("dep orders shouyld
// always open on the ALL tab") — `pages/DepartmentOrders.js` now seeds
// `activeTab` from a literal, never from storage, so these ids are written
// nowhere. They stay lower-kebab regardless: it matches the localStorage
// convention's own `sfx_{page}_{setting}` spelling and keeps them URL-safe
// if a `?tab=` deep link is ever wanted.
// ---------------------------------------------------------------------------
export const ORDER_QUEUE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'to-approve', label: 'To approve' },
  { id: 'to-order', label: 'To order' },
  { id: 'to-receive', label: 'To receive' },
  { id: 'to-complete', label: 'To complete' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export const ORDER_QUEUE_TAB_IDS = ORDER_QUEUE_TABS.map((t) => t.id);

/**
 * `orderQueueTab(order)` — THE status→queue mapping. Returns one of the SIX
 * non-`all` tab ids, or `null` for an order whose status this function does
 * not recognise.
 *
 * 🔑 **IT NEVER RETURNS `'all'`.** "All" means EVERYTHING, so it is the
 * absence of a filter rather than a bucket — the page reads
 * `activeTab === 'all'` and applies no predicate at all. A function that could
 * answer `'all'` would turn that tab into a narrowing and hide every order it
 * did not hand that answer to, which is the opposite of what All means.
 *
 * 🔁 **`null` NOW MEANS "UNRECOGNISED", AND IT MEANT "All ONLY" UNTIL
 * 2026-09-12 AFTERNOON — the ruling reversed, so read the dates.**
 * *2026-09-12 morning:* *"leave rejected and cancelled in All only"* — both
 * answered `null`, on the reasoning that a rejected order is bounced back to
 * its requester and a cancelled one is not coming, so neither is anybody's
 * queue and putting them in one would put dead work in front of a buyer every
 * day. *2026-09-12 afternoon:* the owner looked at the strip on production,
 * found the queue counts short of `All` by exactly those rows, and asked for a
 * Cancelled tab so the counts add up. Both now answer `'cancelled'`.
 * **The dead-work argument was never wrong — it is why the pair shares ONE
 * tab, placed LAST, rather than two tabs beside the live queues.**
 *
 * 🪤 So `null` is now reachable ONLY through a status this function has never
 * heard of, which means the sum identity above (`All` = the six queue counts)
 * is a claim about recognised statuses. A status added to `ORDER_STATUSES` and
 * not to this switch breaks it — §1 of `statuses.orderQueueTab.test.js` fails
 * by name before it can ship.
 *
 * 🚨 **COMPLETION IS CHECKED FIRST, AND TWO OWNER RULES OVERLAP HERE.**
 * "Completed = `isOrderComplete` true (ANY status)" and "rejected and cancelled
 * go in Cancelled" both claim a cancelled-but-complete row. That row is
 * UNREACHABLE: `PATCH /:id/cancel` clears `completed_at` in the same
 * transaction as the status write (grep `status_before_cancel` in
 * `server/routes/orders.js`), and `rejected` precedes delivery, so the server
 * cannot produce either pair. The precedence is written down anyway — in code
 * and in §4 of `statuses.orderQueueTab.test.js` — because a precedence that
 * falls out of statement order is not a decision, and the next person to make
 * one of those pairs reachable needs to find the argument rather than re-run it.
 *
 * 🪤 **FAILS QUIET on anything it does not recognise** — `null`, `undefined`, a
 * row with no `status`, a status added to the schema and not to this function
 * all answer `null`, i.e. "All only". The alternative (throwing, or defaulting
 * into a queue) puts an unclassified order into somebody's work list or takes
 * the page down; being visible in All and nowhere else is the recoverable
 * failure. §1 of the test file is what stops that quiet default becoming a
 * silent gap: it derives its table from `ORDER_STATUSES` and fails by name.
 *
 * @param {{ status?: string, completed_at?: string|null }|null|undefined} order
 * @returns {'to-approve'|'to-order'|'to-receive'|'to-complete'|'completed'|'cancelled'|null}
 */
export function orderQueueTab(order) {
  if (!order) return null;
  // The overlay first — see the precedence note above. ONE shared predicate;
  // never re-read `completed_at` here.
  if (isOrderComplete(order)) return 'completed';
  switch (order.status) {
    case 'submitted': return 'to-approve';
    case 'approved': return 'to-order';
    case 'ordered':
    case 'partially_delivered': return 'to-receive';
    case 'delivered': return 'to-complete';
    // 🔁 Both, into the ONE tab, since 2026-09-12 afternoon — see the docblock.
    case 'rejected':
    case 'cancelled': return 'cancelled';
    // Anything unrecognised: All only.
    default: return null;
  }
}

/**
 * `orderQueueTabs(order)` — EVERY Dept Orders queue an order belongs to (owner
 * 2026-10-28: "If an order has 4 items and I mark 2 as ordered shoudl the order
 * appear in to order AND to recieve? I think so"). Its `orderQueueTab` queue,
 * PLUS `to-receive` when an APPROVED order (still lines to order) already has a
 * line ordered and not yet delivered — the server's `line_progress`. So the
 * queue counts may now add up to MORE than All, by design. The toast and the
 * deep link still use the single `orderQueueTab` (one destination).
 */
export function orderQueueTabs(order) {
  const primary = orderQueueTab(order);
  if (primary == null) return [];
  const lp = order && order.line_progress;
  if (primary === 'to-order' && lp && Number(lp.ordered_items) - Number(lp.received_items || 0) > 0) {
    return ['to-order', 'to-receive'];
  }
  return [primary];
}

// ---------------------------------------------------------------------------
// MY ORDERS TABS (owner ruling 2026-09-30) — the REQUESTER's tab strip.
//
// Owner, in order: **Awaiting approval (submitted) · On order (approved,
// ordered — nothing received) · Deliveries (partially_delivered, delivered) ·
// Needs action (rejected) · Cancelled · Completed · All** — since 2026-09-30 All is rendered FIRST (owner), see below, each with a count,
// every order in exactly ONE tab (All aside). It replaced the All/Actionable
// toggle AND the status Filters popover.
//
// 🔑 A SEPARATE VOCABULARY FROM `ORDER_QUEUE_TABS`, ON PURPOSE. Department
// Orders' tabs name the BUYER's queues ("To order" = waiting for somebody to
// place it); these name what the REQUESTER is waiting for. The same status
// lands in differently named tabs on the two pages, and `rejected` is its own
// tab here (it is YOUR work — resubmit it) where Department Orders files it
// under Cancelled as dead work. Unifying the two lists would mislabel one page.
//
// 🚨 COMPLETED IS TWO THINGS, AND BOTH ARE CHECKED FIRST: the buyer's
// completion overlay (`isOrderComplete`, `orders.completed_at`) OR the
// requester's own "Mark as done" (`order.my_done === true`, a per-user flag
// the server keeps in `order_personal_done` and NEVER on the order — nobody
// else's view moves). Same precedence argument as `orderQueueTab`: completion
// cuts across the status axis, so it must be tested before the switch.
// 🪤 `my_done === true`, never truthy — a list row from an endpoint that does
// not send the flag must not land in Completed by accident.
// ---------------------------------------------------------------------------
export const MY_ORDER_TABS = [
  // 🔀 `All` is FIRST (owner, 2026-09-30, mobile polish) — it was last. It is
  // still the default tab; only its position moved.
  { id: 'all', label: 'All' },
  { id: 'awaiting-approval', label: 'Awaiting approval' },
  { id: 'on-order', label: 'On order' },
  { id: 'deliveries', label: 'Deliveries' },
  { id: 'needs-action', label: 'Needs action' },
  // 🔀 Completed BEFORE Cancelled (owner, 2026-10-04).
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export const MY_ORDER_TAB_IDS = MY_ORDER_TABS.map((t) => t.id);

/**
 * `myOrderTab(order)` — THE status→tab mapping for My Orders. Returns one of
 * the SIX non-`all` ids, or `null` for a status it does not recognise (visible
 * in All only — the same quiet failure `orderQueueTab` chose, for the same
 * reason). Never answers `'all'`: All is the absence of a filter.
 *
 * @param {{ status?: string, completed_at?: string|null, my_done?: boolean }|null|undefined} order
 */
export function myOrderTab(order) {
  if (!order) return null;
  if (isOrderComplete(order) || order.my_done === true) return 'completed';
  switch (order.status) {
    case 'submitted': return 'awaiting-approval';
    case 'approved':
    case 'ordered': return 'on-order';
    case 'partially_delivered':
    case 'delivered': return 'deliveries';
    case 'rejected': return 'needs-action';
    case 'cancelled': return 'cancelled';
    default: return null;
  }
}

/**
 * `myOrderTabFromStatusParam(status)` — THE `?status=` → My Orders tab rule,
 * for links from the dashboard (the Outstanding / Unapproved cards, the status
 * pie, the status-breakdown pills). A real status maps through `myOrderTab`, so
 * a link can never land on a tab that disagrees with the rows; anything else —
 * blank, `all`, an unknown value — lands on All.
 *
 * 🔀 `outstanding` → **All** (fix round 2026-09-30, a CONTRACT change: it went
 * to On order the day the tabs shipped). The Outstanding card counts every
 * order NOT IN delivered/cancelled — Awaiting approval, On order, Deliveries
 * (partial) and Needs action at once — so no single tab matches its number,
 * and On order showed fewer rows than the card promised.
 *
 * @param {string|null|undefined} status the raw `?status=` value
 * @returns {string} a `MY_ORDER_TABS` id
 */
export function myOrderTabFromStatusParam(status) {
  const st = status || '';
  if (!st || st === 'all' || st === 'outstanding') return 'all';
  return myOrderTab({ status: st }) || 'all';
}

/** The tabs whose orders the requester may "Mark as done" (owner ruling 2026-09-30). */
export const MY_ORDER_DONEABLE_TABS = ['deliveries', 'cancelled'];

/**
 * May the requester press "Mark as done" on this row? A CONVENIENCE for the
 * menu, never a gate — `PATCH /api/orders/:id/my-done` re-checks the requester
 * and the status (`server/helpers/orderPersonalDone.js`).
 */
export function canMarkOrderDone(order, viewerUserId) {
  if (!order || order.my_done === true || isOrderComplete(order)) return false;
  if (viewerUserId == null || Number(order.user_id) !== Number(viewerUserId)) return false;
  return MY_ORDER_DONEABLE_TABS.includes(myOrderTab(order));
}

/**
 * May the requester press "Restore"? Only on their OWN mark, and only while the
 * buyer has not completed the order too — a Restore that left the row in
 * Completed (because `completed_at` still holds it there) would look broken.
 */
export function canRestoreOrderDone(order, viewerUserId) {
  if (!order || order.my_done !== true || isOrderComplete(order)) return false;
  return viewerUserId != null && Number(order.user_id) === Number(viewerUserId);
}

/** The tooltip on "Mark as done" — the owner's wording, verbatim (2026-09-30). */
export const MARK_ORDER_DONE_TOOLTIP =
  "Moves this order to your Completed tab. It doesn't change the order for anyone else.";
